import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';
import { NotificationsService } from 'angular2-notifications';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContractsApiService } from 'src/app/core/services/api/contracts-api.service';
import { CONTRACT_STATUSES } from 'src/app/modules/contracts/consts/contract-statuses.const';
import { InputFieldType } from 'src/app/shared/models/input-types.enum';
import { FUNDING_STATUSES } from 'src/app/shared/models/contract-email-template.interface';
import { ContractBulkStatusUpdateItem } from 'src/app/shared/models/contract.interface';

@UntilDestroy()
@Component({
  selector: 'app-bulk-change-contract-status-modal',
  templateUrl: './bulk-change-contract-status-modal.component.html',
  styleUrls: ['./bulk-change-contract-status-modal.component.scss'],
})
export class BulkChangeContractStatusModalComponent implements OnChanges {
  @Input() visible = false;
  @Input() contractIds: number[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onConfirm = new EventEmitter<void>();

  form!: FormGroup;
  isLoadingSubmit = false;
  serverError: string | null = null;
  InputFieldType = InputFieldType;

  statusOptions = [
    ...CONTRACT_STATUSES.filter((s) => s.id !== 'cancelled'),
    { id: 'terminated', name: 'Terminated' },
  ].map((s) => ({ label: s.name, value: s.id }));

  fundStatusOptions = FUNDING_STATUSES.map((s) => ({
    label: s.label,
    value: s.value,
  }));

  constructor(
    private fb: FormBuilder,
    private contractsApiService: ContractsApiService,
    private notificationService: NotificationsService
  ) {
    this.initForm();
  }

  ngOnChanges(): void {
    if (this.visible) {
      this.serverError = null;
      this.form.reset({ status: null, fund_status: null, note: '' });
    }
  }

  private initForm(): void {
    this.form = this.fb.group(
      {
        status: [null],
        fund_status: [null],
        note: [''],
      },
      { validators: this.atLeastOneSelectedValidator.bind(this) }
    );
  }

  private atLeastOneSelectedValidator(
    group: AbstractControl
  ): ValidationErrors | null {
    const status = group.get('status')?.value;
    const fundStatus = group.get('fund_status')?.value;
    return status || fundStatus ? null : { noSelection: true };
  }

  get hasNoSelectionError(): boolean {
    return (
      this.form.hasError('noSelection') &&
      (this.form.get('status')?.touched ||
        this.form.get('fund_status')?.touched ||
        this.form.touched)
    );
  }

  onSubmit(): void {
    if (this.form.invalid || !this.contractIds.length) {
      this.form.markAllAsTouched();
      return;
    }

    const { status, fund_status, note } = this.form.value;
    const trimmedNote = note?.trim();

    const contracts: ContractBulkStatusUpdateItem[] = this.contractIds.map(
      (id) => {
        const item: ContractBulkStatusUpdateItem = { id };
        if (status) {
          item.status = status;
        }
        if (fund_status) {
          item.fund_status = fund_status;
        }
        if (trimmedNote) {
          item.note = trimmedNote;
        }
        return item;
      }
    );

    this.isLoadingSubmit = true;
    this.serverError = null;

    this.contractsApiService
      .bulkUpdateContractStatus({ contracts })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.isLoadingSubmit = false;
          this.notificationService.success(
            '',
            response?.message ||
              `${this.contractIds.length} contract${
                this.contractIds.length === 1 ? '' : 's'
              } updated successfully`
          );
          this.closeModal();
          this.onConfirm.emit();
        },
        error: (error) => {
          this.isLoadingSubmit = false;
          this.serverError =
            error?.error?.message ||
            'Failed to update selected contracts. Please try again.';
        },
      });
  }

  closeModal(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetModalState();
  }

  onDialogHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetModalState();
  }

  private resetModalState(): void {
    this.form.reset({ status: null, fund_status: null, note: '' });
    this.isLoadingSubmit = false;
    this.serverError = null;
  }
}
